// @ts-nocheck
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {Eye, EyeSlash} from "@phosphor-icons/react";
import {z} from "zod";
import {toast} from "@/components";
import useUpdatePassword from "../../../hooks/useUpdatePassword";

const buildSchema = t =>
  z
    .object({
      current_password: z.string().min(1, t("changePasswordMessage")),
      new_password: z.string().min(1, t("changePasswordMessage")),
      confirm_password: z.string().min(1, t("changePasswordMessage")),
    })
    .refine(data => data.new_password === data.confirm_password, {
      message: t("changePasswordError"),
      path: ["confirm_password"],
    });

// ─── Styles ──────────────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%",
  height: 48,
  borderRadius: 999,
  background: "var(--color-surface)",
  border: "1.5px solid var(--color-border)",
  color: "var(--color-text)",
  paddingLeft: 20,
  paddingRight: 52,
  fontSize: "var(--font-body-lg)",
  fontFamily: "var(--font-family)",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const wrapBase = {
  position: "relative",
  borderRadius: 999,
  transition: "box-shadow 0.2s",
};

const wrapFocused = {
  boxShadow: "0 0 0 2px #d732a8, 0 0 16px rgba(215,50,168,0.35)",
};

const eyeBtnStyle = {
  position: "absolute",
  right: 16,
  top: "50%",
  transform: "translateY(-50%)",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 0,
  display: "flex",
  alignItems: "center",
};

// ─── PasswordInput ────────────────────────────────────────────────────────────
const PasswordInput = ({placeholder, value, onChange, error}) => {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);

  return (
    <div style={{marginBottom: error ? 4 : 12}}>
      <div style={focused ? {...wrapBase, ...wrapFocused} : wrapBase}>
        <input
          type={show ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoComplete="new-password"
          style={{
            ...inputStyle,
            borderColor: error ? "var(--color-danger)" : focused ? "#d732a8" : "var(--color-border)",
          }}
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
          style={eyeBtnStyle}
        >
          {show
            ? <Eye size={20} color="#d732a8" weight="bold" />
            : <EyeSlash size={20} color="rgba(255,255,255,0.35)" />
          }
        </button>
      </div>
      {error && (
        <p style={{color: "var(--color-danger)", fontSize: 12, marginTop: 4, marginBottom: 8, paddingLeft: 16}}>
          {error}
        </p>
      )}
    </div>
  );
};

// ─── SubmitButton ─────────────────────────────────────────────────────────────
const SubmitButton = ({loading, children}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="submit"
      disabled={loading}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        height: 48,
        minWidth: 160,
        borderRadius: 999,
        border: hovered ? "1.5px solid transparent" : "1.5px solid rgba(215,50,168,0.6)",
        background: hovered ? "linear-gradient(135deg,#fa5400,#d732a8)" : "transparent",
        color: hovered ? "#fff" : "#d732a8",
        fontWeight: 700,
        fontSize: "var(--font-body-lg)",
        fontFamily: "var(--font-family)",
        padding: "0 32px",
        cursor: loading ? "not-allowed" : "pointer",
        opacity: loading ? 0.6 : 1,
        transition: "background 0.2s, border-color 0.2s, color 0.2s",
      }}
    >
      {loading ? "..." : children}
    </button>
  );
};

// ─── Form ─────────────────────────────────────────────────────────────────────
const ChangePasswordForm = () => {
  const {postUpdate, isPending} = useUpdatePassword();
  const {t} = useTranslation();

  const [values, setValues] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = field => e => {
    setValues(v => ({...v, [field]: e.target.value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };

  const handleSubmit = e => {
    e.preventDefault();
    const result = buildSchema(t).safeParse(values);
    if (!result.success) {
      const nextErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    postUpdate({
      old_password: result.data.current_password,
      new_password: result.data.new_password,
    });
    toast.success("Contraseña actualizada");
  };

  return (
    <form
      style={{width: "100%", marginTop: 16, paddingLeft: 50, boxSizing: "border-box"}}
      onSubmit={handleSubmit}
      autoComplete="off"
      noValidate
    >
      <PasswordInput
        placeholder={t("changeCurrentPassword")}
        value={values.current_password}
        onChange={handleChange("current_password")}
        error={errors.current_password}
      />
      <PasswordInput
        placeholder={t("changeNewPassword")}
        value={values.new_password}
        onChange={handleChange("new_password")}
        error={errors.new_password}
      />
      <PasswordInput
        placeholder={t("changeConfirmNewPassword")}
        value={values.confirm_password}
        onChange={handleChange("confirm_password")}
        error={errors.confirm_password}
      />

      <div style={{display: "flex", justifyContent: "center", marginTop: 8, paddingRight: 50}}>
        <SubmitButton loading={isPending}>{t("changePasswordBtn")}</SubmitButton>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
